'use client';

import type { CSSProperties } from 'react';
import { Link } from '@/components/ui';
import { useResolveThemeColor } from '@/hooks/use-resolve-theme-color';
import { cn } from '@/utils';
import { GUMLET_PRESETS } from '@/utils/gumlet';
import { selectMainImage } from '@/utils/select-main-image';

export type ImageGridItem = {
  label: string;
  src: string;
  href: string;
};

export type ImageGridSectionProps = {
  /** Section height (e.g. "400px", "50vh", "auto") */
  height: string;
  /** Section background (CSS color or value for background) */
  background: string;
  /** Section title - same style options as hero title */
  title: {
    text: string;
    color: string;
    fontSize: string;
    fontWeight: string;
  };
  items: ImageGridItem[];
  paddingStyle?: CSSProperties;
};

const ImageGridSection = ({
  height,
  background,
  title,
  items,
  paddingStyle,
}: ImageGridSectionProps) => {
  const resolveThemeColor = useResolveThemeColor();
  return (
    <section
      className="flex w-full flex-col items-center justify-center gap-8"
      style={{
        minHeight: height,
        backgroundColor: resolveThemeColor(background),
        ...paddingStyle,
      }}
    >
      {title.text && (
        <h2
          className={cn('text-center font-semibold', {
            'font-normal': title.fontWeight === 'light',
            'font-medium': title.fontWeight === 'normal',
            'font-bold': title.fontWeight === 'bold' || title.fontWeight === 'semi-bold',
          })}
          style={{ color: resolveThemeColor(title.color), fontSize: title.fontSize }}
        >
          {title.text}
        </h2>
      )}
      <div className="flex w-full max-w-6xl flex-wrap justify-center gap-3">
        {items.map((item, index) => {
          const imageSrc = selectMainImage([item.src], GUMLET_PRESETS.gridTile);
          return (
            <Link
              noStyle
              key={`${item.href}-${index}`}
              href={item.href}
              className="group focus:ring-primary relative aspect-[4/3] w-[calc(50%-0.375rem)] min-w-0 shrink-0 overflow-hidden rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none @sm:w-[calc((100%-0.75rem)/3)] @md:w-[calc((100%-1.5rem)/4)] @lg:w-[calc((100%-3rem)/5)]"
            >
              <span
                className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-300 group-hover:scale-105"
                style={{ backgroundImage: `url(${imageSrc})` }}
              />
              <span className="absolute inset-0 bg-black/50" aria-hidden />
              <span className="absolute inset-0 flex items-center justify-center p-3 text-center text-sm font-medium tracking-wide whitespace-pre-line text-white uppercase">
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default ImageGridSection;
